import { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Tab, Tabs, Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import { apiConstants, getGeneric, postGeneric } from "../../common/constants";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { PRIMARY_BLUE } from "../../styles/foundation/colors";
import { getCategoryConfig } from "./escalationConfigCache";
import { gradientBtnSx } from "./ticketFormStyles";

// The merchant panel's Bulk Create: one ticket per sheet row, always for the signed-in merchant
// and shown on the merchant panel (the server enforces both). Same background job as the admin
// panel's Bulk Create (POST /escalation/bulk-create/jobs), polled for progress; closing the
// dialog doesn't stop it, and reopening picks it back up.

const SAMPLE_ROWS = [
    { category: "NDR", subcategory: "Order not getting Re-attempted", awb_number: "14408921119333",
        description: "Customer is available, please re-attempt", cc_emails: "ops@yourbrand.com",
        attachment_links: "https://drive.google.com/file/d/1AbCdEfGhIjKlMnOp/view?usp=sharing" },
    { category: "Billing and Invoice", subcategory: "Invoice not received", awb_number: "", description: "", cc_emails: "", attachment_links: "" },
];
const KNOWN_COLUMNS = ["category", "subcategory", "awb_number", "description", "cc_emails", "attachment_links"];
// The server downloads the files itself; for a merchant only these share links (EscalationDocService).
const SHARE_LINK = /^https:\/\/((drive|docs)\.google\.com|(www\.)?dropbox\.com)\/.*/i;
const MAX_LINKS = 10;
const JOBS = `${apiConstants.ESCALATION_DETAIL_V2}/bulk-create/jobs`;
const FINISHED = ["DONE", "CANCELLED", "INTERRUPTED", "FAILED"];
const POLL_MS = 2000;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MUTED = "#6B7A90";

const STATUS_META = {
    QUEUED: { label: "Starting", bg: "#EEF2F7", color: "#41556B" },
    RUNNING: { label: "In progress", bg: "#E8F0FE", color: PRIMARY_BLUE },
    DONE: { label: "Finished", bg: "#E8F5E9", color: "#2E7D32" },
    CANCELLED: { label: "Stopped", bg: "#FFF4E5", color: "#B45309" },
    INTERRUPTED: { label: "Interrupted", bg: "#FFF4E5", color: "#B45309" },
    FAILED: { label: "Stopped by an error", bg: "#FDECEA", color: "#C0392B" },
};
const STATUS_NOTE = {
    CANCELLED: "You stopped the upload. Rows without a result weren't created.",
    INTERRUPTED: "The server restarted during the upload. Rows without a result weren't created — upload those again.",
    FAILED: "The upload stopped because of an error. Rows without a result weren't created.",
};

const text = (v) => (v == null ? "" : v.toString().trim());
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The running job is remembered per merchant, so switching merchant never shows another's upload.
const storageKey = () => `ems_merchant_bulk_create_job_${getMerchant()?.id || ""}`;
const remember = (id) => { try { id ? localStorage.setItem(storageKey(), id) : localStorage.removeItem(storageKey()); } catch (e) { /* storage off */ } };
const remembered = () => { try { return localStorage.getItem(storageKey()); } catch (e) { return null; } };

const readSheet1 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        try {
            const workbook = XLSX.read(e.target.result, { type: "binary", cellDates: true });
            const sheet = workbook.Sheets.Sheet1 || workbook.Sheets[workbook.SheetNames[0]];
            const rows = sheet ? XLSX.utils.sheet_to_json(sheet, { raw: false, defval: "" }) : [];
            resolve(rows.filter((row) => Object.values(row).some((v) => text(v))));
        } catch (err) {
            reject(err);
        }
    };
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.readAsBinaryString(file);
});

const downloadSheet = (rows, fileName) => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Sheet1");
    XLSX.writeFile(workbook, fileName);
};

const BulkCreateTickets = ({ open, onClose, onDone }) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);
    // Bumped on every stop, so a status GET still in flight when the dialog closes drops its answer.
    const pollGenRef = useRef(0);
    const openRef = useRef(open);
    const sheetRef = useRef({}); // rowNumber -> sheet row, for "Download failed rows"
    const localFailCountRef = useRef(0);
    const [fileName, setFileName] = useState("");
    const [checking, setChecking] = useState(false);
    const [localFailures, setLocalFailures] = useState([]);
    const [job, setJob] = useState(null);
    const [tab, setTab] = useState("FAILED");

    const running = !!job && !FINISHED.includes(job.status);

    const stopPolling = () => {
        pollGenRef.current += 1;
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = null;
    };

    const poll = useCallback(async (jobId) => {
        stopPolling();
        if (!openRef.current) return;
        const gen = pollGenRef.current;
        try {
            const res = await getGeneric(`${JOBS}/${jobId}`);
            if (gen !== pollGenRef.current) return;
            const next = res?.data;
            setJob(next);
            if (next && !FINISHED.includes(next.status)) {
                pollRef.current = setTimeout(() => poll(jobId), POLL_MS);
            } else if (next) {
                const rows = next.total + localFailCountRef.current;
                enqueueSnackbar(`Bulk create finished — ${next.successCount} of ${rows} tickets created.`,
                    { variant: next.successCount === rows && next.status === "DONE" ? "success" : "warning" });
                onDone?.();
            }
        } catch (e) {
            if (gen !== pollGenRef.current) return;
            remember(null);
            setJob(null);
        }
    }, [enqueueSnackbar, onDone]);

    useEffect(() => {
        openRef.current = open;
        if (!open) { stopPolling(); return undefined; }
        const id = remembered();
        if (id && !job) poll(id);
        return () => { openRef.current = false; stopPolling(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const reset = () => {
        stopPolling();
        remember(null);
        sheetRef.current = {};
        localFailCountRef.current = 0;
        setFileName("");
        setLocalFailures([]);
        setJob(null);
        setTab("FAILED");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = () => {
        if (!running) reset();
        else stopPolling();
        onClose();
    };

    // Checks what the browser can; the server checks the rest (AWB belongs to you, duplicates, …).
    // catalog: lower-cased category name -> { name, subs } (active ones only).
    const checkRow = (row, catalog) => {
        const category = text(row.category);
        const subCategory = text(row.subcategory);
        if (!category || !subCategory) return { error: "Category or subcategory is missing" };
        const entry = catalog.get(category.toLowerCase());
        if (!entry) return { error: `Category "${category}" not found` };
        const sub = entry.subs.find((s) => s.toLowerCase() === subCategory.toLowerCase());
        if (!sub) return { error: `Subcategory "${subCategory}" not found under ${category}` };
        const awb = text(row.awb_number);
        if (/[,\s]/.test(awb)) return { error: "Only one AWB per ticket — put each AWB on its own row" };
        const cc = text(row.cc_emails).split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
        const badEmail = cc.find((e) => !EMAIL.test(e));
        if (badEmail) return { error: `"${badEmail}" isn't a valid email` };
        const links = text(row.attachment_links).split(/[,\s]+/).map((l) => l.trim()).filter(Boolean);
        if (links.some((l) => !SHARE_LINK.test(l))) return { error: "Attachment links must be Google Drive or Dropbox share links" };
        if (links.length > MAX_LINKS) return { error: `At most ${MAX_LINKS} attachment links per ticket` };
        const customFieldValues = {};
        Object.keys(row).filter((k) => !KNOWN_COLUMNS.includes(k) && text(row[k]))
            .forEach((k) => { customFieldValues[k] = text(row[k]); });
        return {
            item: {
                category: entry.name,
                subCategory: sub,
                awbNumber: awb || undefined,
                description: text(row.description) || undefined,
                additionalEmails: cc,
                attachmentLinks: links,
                customFieldValues,
            },
        };
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_BYTES) {
            enqueueSnackbar("File is larger than the 2 MB limit.", { variant: "error" });
            e.target.value = "";
            return;
        }
        reset();
        setFileName(file.name);
        setChecking(true);
        try {
            const [sheet, categories] = await Promise.all([readSheet1(file), getCategoryConfig()]);
            if (!sheet.length) {
                enqueueSnackbar("No rows found in Sheet1.", { variant: "warning" });
                return;
            }
            const catalog = new Map((categories || []).filter((c) => c.active !== false).map((c) => [c.categoryName.toLowerCase(),
                { name: c.categoryName, subs: (c.subCategories || []).filter((s) => s.active !== false).map((s) => s.name) }]));
            const failures = [];
            const valid = [];
            sheet.forEach((row, i) => {
                const rowNumber = i + 2; // header is row 1
                sheetRef.current[rowNumber] = row;
                const label = text(row.awb_number);
                const checked = checkRow(row, catalog);
                if (checked.error) failures.push({ rowNumber, label, status: "FAILED", message: checked.error });
                else valid.push({ rowNumber, label, item: checked.item });
            });
            setLocalFailures(failures);
            localFailCountRef.current = failures.length;
            if (!valid.length) {
                enqueueSnackbar("None of the rows can be created — see the reasons below.", { variant: "warning" });
                return;
            }
            const res = await postGeneric(JOBS, { rows: valid });
            remember(res.data.id);
            setJob(res.data);
            poll(res.data.id);
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || err.message || "Bulk create couldn't start.", { variant: "error" });
        } finally {
            setChecking(false);
        }
    };

    const stop = async () => {
        try {
            await postGeneric(`${JOBS}/${job.id}/cancel`, {});
            enqueueSnackbar("Stopping after the rows in progress…", { variant: "info" });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Couldn't stop the upload", { variant: "error" });
        }
    };

    const all = [...localFailures, ...((job && job.results) || [])].sort((a, b) => a.rowNumber - b.rowNumber);
    const failedRows = all.filter((r) => r.status !== "SUCCESS");
    const createdRows = all.filter((r) => r.status === "SUCCESS");
    const total = (job?.total || 0) + localFailures.length;
    const done = (job?.processed || 0) + localFailures.length;
    const pct = total ? Math.round((done * 100) / total) : 0;
    const started = !!job || localFailures.length > 0;
    const statusMeta = job ? STATUS_META[job.status] : (localFailures.length ? STATUS_META.DONE : null);
    const shown = tab === "FAILED" ? failedRows : createdRows;

    const downloadFailed = () => {
        const rows = failedRows.map((r) => ({ ...(sheetRef.current[r.rowNumber] || { awb_number: r.label }), sheet_row: r.rowNumber, reason: r.message }));
        downloadSheet(rows, "Bulk_Create_Failed_Rows.xlsx");
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Bulk Create Tickets
                <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", p: 2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>One ticket per row, in <b>Sheet1</b> of a CSV or Excel file.</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
                        Up to 5,000 rows · file up to 2 MB · columns: <b>category</b>, <b>subcategory</b> (required), <b>awb_number</b>,
                        {" "}<b>description</b>, <b>cc_emails</b>, <b>attachment_links</b> (Google Drive or Dropbox links shared as
                        &quot;Anyone with the link&quot;, comma separated, 20 MB per ticket). Any other column is the subcategory&apos;s own field.
                        Updates go to your login email. Attachments can be added on each ticket afterwards.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5, alignItems: "center" }}>
                        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={running || checking}
                            sx={{ textTransform: "none", borderRadius: "999px" }}>
                            {fileName || "Choose file"}
                            <input ref={fileInputRef} type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFile} />
                        </Button>
                        <Button variant="text" startIcon={<GetAppIcon />} sx={{ textTransform: "none", fontWeight: 700 }}
                            onClick={() => downloadSheet(SAMPLE_ROWS, "Bulk_Create_Sample.xlsx")}>
                            Download sample
                        </Button>
                    </Box>
                </Box>

                {checking && (
                    <Box>
                        <LinearProgress sx={{ height: 4, borderRadius: 4 }} />
                        <Typography sx={{ fontSize: 13, color: MUTED, mt: 1 }}>Checking the sheet…</Typography>
                    </Box>
                )}

                {started && (
                    <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                {pct}% · {done.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")} rows
                            </Typography>
                            {statusMeta && <Chip size="small" label={statusMeta.label} sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700, fontSize: 12 }} />}
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
                            {[
                                { label: "Created", value: createdRows.length, color: "#2E7D32", bg: "#E8F5E9" },
                                { label: "Failed", value: failedRows.length, color: "#C0392B", bg: "#FDECEA" },
                                { label: "Remaining", value: Math.max(0, total - done), color: "#41556B", bg: "#F4F6F9" },
                            ].map((t) => (
                                <Box key={t.label} sx={{ bgcolor: t.bg, borderRadius: "10px", px: 1.5, py: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: t.color, fontWeight: 600 }}>{t.label}</Typography>
                                    <Typography sx={{ fontSize: 18, color: t.color, fontWeight: 700 }}>{t.value.toLocaleString("en-IN")}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {job && STATUS_NOTE[job.status] && <Typography sx={{ fontSize: 12, color: "#B45309", mt: 1.25 }}>{STATUS_NOTE[job.status]}</Typography>}
                        {running && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, gap: 1 }}>
                                <Typography sx={{ fontSize: 12, color: MUTED }}>Runs on the server — you can close this window and reopen Bulk Create to check on it.</Typography>
                                <Button size="small" color="error" onClick={stop} disabled={job?.cancelRequested} sx={{ textTransform: "none", flexShrink: 0 }}>
                                    {job?.cancelRequested ? "Stopping…" : "Stop upload"}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {all.length > 0 && (
                    <Box sx={{ border: "1px solid #E7EAF0", borderRadius: "12px", overflow: "hidden" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #E7EAF0", pr: 1 }}>
                            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: 13 } }}>
                                <Tab value="FAILED" label={`Failed (${failedRows.length})`} />
                                <Tab value="SUCCESS" label={`Created (${createdRows.length})`} />
                            </Tabs>
                            {tab === "FAILED" && failedRows.length > 0 && (
                                <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadFailed} sx={{ textTransform: "none", fontWeight: 700 }}>
                                    Download failed rows
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                            {shown.length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: MUTED, p: 2, textAlign: "center" }}>
                                    {tab === "FAILED" ? "No failed rows." : "No tickets created yet."}
                                </Typography>
                            ) : (
                                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13,
                                    "& th": { textAlign: "left", color: MUTED, fontWeight: 600, fontSize: 12, px: 1.5, py: 0.75, bgcolor: "#F9FAFB", position: "sticky", top: 0 },
                                    "& td": { px: 1.5, py: 0.75, borderTop: "1px solid #F1F3F6", verticalAlign: "top" } }}>
                                    <thead><tr><th style={{ width: 56 }}>Row</th><th style={{ width: 130 }}>AWB</th><th>{tab === "FAILED" ? "Reason" : "Ticket"}</th></tr></thead>
                                    <tbody>
                                        {shown.map((r) => (
                                            <tr key={r.rowNumber}>
                                                <td style={{ color: MUTED }}>{r.rowNumber}</td>
                                                <td>{r.label || "—"}</td>
                                                <td style={{ color: tab === "FAILED" ? "#C0392B" : "#2E7D32", fontWeight: tab === "FAILED" ? 400 : 600 }}>
                                                    {tab === "FAILED" ? r.message : r.escalationNumber}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {!running && started && <Button onClick={reset} sx={{ textTransform: "none", fontWeight: 700 }}>Upload another sheet</Button>}
                <Button variant="contained" onClick={handleClose} sx={gradientBtnSx}>{running ? "Close — keeps running" : "Done"}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkCreateTickets;
